import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import { cn } from '../lib/utils';

function Dialog({ dataTestId, ...props }) {
  return <DialogPrimitive.Root data-slot="dialog" data-testid={dataTestId} {...props} />;
}

function DialogTrigger({ dataTestId, ...props }) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" data-testid={dataTestId} {...props} />;
}

function DialogPortal({ dataTestId, ...props }) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" data-testid={dataTestId} {...props} />;
}

function DialogClose({ dataTestId, ...props }) {
  return <DialogPrimitive.Close data-slot="dialog-close" data-testid={dataTestId} {...props} />;
}

function DialogOverlay({ className, dataTestId, ...props }) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      data-testid={dataTestId}
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-overlay',
        className
      )}
      {...props}
    />
  );
}

// `closeButtonProps` is spread onto the built-in close button so a caller can give it a test id or
// disable it (e.g. while a save is in flight) without hand-rolling its own close control.
function DialogContent({ className, children, dataTestId, closeButtonProps, ...props }) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-testid={dataTestId}
        className={cn(
          'bg-surface-panel data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg',
          className
        )}
        {...props}
      >
        {children}
        {/* bg/text are the tokens whose LIGHT values are the #E6F1FE / #0062e7 this used to hardcode,
            so light is unchanged and dark stops painting a bright chip on a dark panel. */}
        <DialogPrimitive.Close
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center cursor-pointer justify-center rounded-full border border-close-line bg-action-chip-bg text-chip-info-fg transition-colors disabled:pointer-events-none disabled:opacity-50"
          {...closeButtonProps}
        >
          <XIcon className="size-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, dataTestId, ...props }) {
  return <div data-slot="dialog-header" data-testid={dataTestId} className={cn('flex flex-col gap-2 text-center sm:text-left', className)} {...props} />;
}

function DialogFooter({ className, dataTestId, ...props }) {
  return (
    <div
      data-slot="dialog-footer"
      data-testid={dataTestId}
      // One action size for every modal footer: 48px tall, 16px label. Set on the container rather
      // than on each Button, because footers here hold a mix of shared (`primaryButtonClassName`)
      // and bare `<Button>` actions — the descendant rule outranks the Button's own `h-9`/`text-sm`
      // on specificity, so it lands on all of them without touching a single call site.
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&_button]:h-12 [&_button]:text-base', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, dataTestId, ...props }) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      data-testid={dataTestId}
      // Matches SheetTitle — a modal and a drawer should not announce themselves differently.
      className={cn('text-xl sm:text-2xl font-bold text-fg', className)}
      {...props}
    />
  );
}

function DialogDescription({ className, dataTestId, ...props }) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      data-testid={dataTestId}
      // Matches SheetDescription.
      className={cn('text-sm text-fg-secondary', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
